import queryString from 'query-string';
import { 
  createApi, 
  fetchBaseQuery,
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError, 
} from '@reduxjs/toolkit/query/react';
import { HYDRATE } from 'next-redux-wrapper'
import { isErrorResponse, redirectWithReturnUrl } from '@/global/utils/apiHelpers';
import { API_PATH, BASE_HOST } from '@/shared/constants/env';
import { TOKEN_KEY, TOKENIZE_URL_START_WITH } from '@/shared/constants/token';
import { isProtectedRoute } from '@/processes/Router';
import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';
import { 
  SECURITY_CODE_TYPES, 
  toggleConfimation,
} from '@/features/Confirmation';

const baseQuery = fetchBaseQuery({ 
  baseUrl: `${API_PATH}`,
  credentials: "include",
  prepareHeaders: (headers) => {
    headers.set("Content-Type", "application/json");
    return headers;
  },
});

// store previous query, to re fetch it with security code
let lastConfirmationQuery: any = {};

const extractTokenFromUrlAndAddToQuery = (args: any) => {
  const { query } = queryString.parseUrl(typeof window !== 'undefined' ? window.location.href : '');
  const token = _get(query, TOKEN_KEY, null);

  // check if api url match allowed path
  const withToken = TOKENIZE_URL_START_WITH.some(pathPrefix => args?.url?.includes(pathPrefix));
  if (!token || !withToken) { return args; } 

  const querySymbol = args?.url?.includes('?') ? '&' : '?';
  const urlWithToken = `${args.url}${querySymbol}${TOKEN_KEY}=${token}`;
  return { ...args, url: urlWithToken };
}

const baseQueryWithConfirmation: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const argsWithToken = extractTokenFromUrlAndAddToQuery(args);
  let result = await baseQuery(argsWithToken, api, extraOptions);
  const { error } = result;
  const protectedRoute = isProtectedRoute();
  // 401 unauthorized and route is protected, redirect to login
  if (
    isErrorResponse(error) 
    && error.status === 401
    && Boolean(protectedRoute)
  ) {
    const redirect = redirectWithReturnUrl({ 
      return_url: protectedRoute,
      redirect: error.data.redirect
    });
    window.location.href = redirect;
  }

  // 403 confirmation required
  if (result.error && result.error.status === 403 && isErrorResponse(result.error)) {
    // clone query parameters
    lastConfirmationQuery = _cloneDeep({ args, api, extraOptions });
    // get confirmation type from header x-security-code-type
    const confirmationType = result.meta?.response?.headers.get('x-security-code-type') || '';
    // check if confirmation type is supported
    // @ts-ignore
    const isValidConfirmationType = Object.values(SECURITY_CODE_TYPES).includes(confirmationType);
    // dispatch action to show confirmation modal
    if (isValidConfirmationType) {
      api.dispatch(toggleConfimation({ isShow: true, confirmationType }));
    }
  }
  
  return result;
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithConfirmation,
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === HYDRATE) {
      return action.payload[reducerPath]
    }
  },
  endpoints: (build) => ({
    fetchLatestConfirmationQuery: build.mutation({
      async queryFn(_args, _queryApi, _extraOptions, baseQuery) {
        const { args } = lastConfirmationQuery;
        // get previous query and add security code to headers
        const result = await baseQuery({
          ...args,
          ...{
            credentials: 'include',
            headers: {
              'x-security-code': _args.code,
            },
          },
        });
        return result;
      },
    }), 
  }),
});

export const {
  useFetchLatestConfirmationQueryMutation,
} = api;

export default api;
